import { CardContent, Grid, Paper, Typography } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyGridContainer } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/KeysIndicators';
import VariationItem from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/VariationItem';
import ResultItemHeader from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/components/SimulationResultItemHeader';
import {
    displayTypeSelector,
    previsionnalVariationSelector,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/dataHelpers';
import {
    TotalResultAmounts,
    variationType,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/types';
import { getAmounts } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/dataHelpers';
import { getGaugeEchartOptions } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/eChartHelpers';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';

import HrGaugeChart from 'routes/HumanResourcesModule/components/HrChart/HrGaugeChart';
import StyCard from 'routes/HumanResourcesModule/components/ui/StyCard';

type Props = {
    totalAmounts: TotalResultAmounts;
    startDate: SimulationDateString;
    year: number;
    isLoading: boolean;
};

const StyCardContainer = styled(StyCard)(() => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: '1',
    height: '100%',
}));
export const SimulationResultCardContent = styled(CardContent)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
});
const StyPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: '1',
    width: '100%',
    padding: '0 1rem',
}));

const StyTypography = styled(Typography)(() => ({
    paddingTop: '15px',
    paddingX: '1rem',
    textAlign: 'center',
}));
const SimulationResultItem = ({
    totalAmounts,
    startDate,
    year,
    isLoading,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isStartYear = parseInt(startDate.substring(0, 4), 10) === year;
    const isPastYear = new Date().getFullYear() > year;
    const {
        initialValue,
        previsionalValue,
        valueToCompare,
        previousYearRealizedAmount,
        realizedAmount,
    } = getAmounts(totalAmounts, isStartYear);

    const options = getGaugeEchartOptions(realizedAmount, initialValue, theme);
    const item = displayTypeSelector(startDate, year);

    return (
        <StyCardContainer>
            <ResultItemHeader
                startDate={startDate}
                isPastYear={isPastYear}
                year={year}
            />
            <SimulationResultCardContent>
                <StyGridContainer
                    container
                    spacing={2}
                    sx={{ display: 'flex', flexGrow: '1', height: '100%' }}
                >
                    <Grid item xs={6} sx={{ display: 'flex', flexGrow: '1' }}>
                        <StyPaper>
                            <StyTypography
                                variant="body1"
                                sx={{
                                    height: '60px',
                                    minHeight: '60px',
                                    maxHeight: '60px',
                                }}
                            >
                                {t(
                                    `humanResources.simulation.keysIndicators.${item}.chartTitle`,
                                    { year },
                                )}
                            </StyTypography>
                            <HrGaugeChart
                                isLoading={isLoading}
                                options={options}
                            />
                        </StyPaper>
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        sx={{
                            display: 'flex',
                            flexGrow: '1',
                        }}
                    >
                        <StyGridContainer container spacing={2}>
                            <Grid item xs={12}>
                                <VariationItem
                                    item={item}
                                    startDate={startDate}
                                    year={year}
                                    isLoading={isLoading}
                                    variation={previsionnalVariationSelector(
                                        startDate,
                                        year,
                                    )}
                                    value={previsionalValue}
                                    valueToCompare={previousYearRealizedAmount}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <VariationItem
                                    item={displayTypeSelector(startDate, year)}
                                    year={year}
                                    startDate={startDate}
                                    isLoading={isLoading}
                                    variation={variationType.INITIAL}
                                    value={initialValue}
                                    valueToCompare={valueToCompare}
                                />
                            </Grid>
                        </StyGridContainer>
                    </Grid>
                </StyGridContainer>
            </SimulationResultCardContent>
        </StyCardContainer>
    );
};

export default memo(SimulationResultItem);
